type OpenSetupListener = (opts?: { step?: number }) => void;

const openListeners = new Set<OpenSetupListener>();
const stateListeners = new Set<() => void>();

type SetupWizardUiState = {
  /** Sheet mounted (session active — not dismissed). */
  visible: boolean;
  /** Panel expanded; false = strip only above companion. */
  expanded: boolean;
  /** Accordion: snap shut so companion expand stays smooth. */
  collapseInstant: boolean;
};

let state: SetupWizardUiState = {
  visible: false,
  expanded: false,
  collapseInstant: false,
};

function emit(): void {
  for (const fn of stateListeners) fn();
}

export function openSetupWizard(opts?: { step?: number }): void {
  for (const fn of openListeners) fn(opts);
}

export function subscribeOpenSetup(listener: OpenSetupListener): () => void {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

export function getSetupWizardUiState(): SetupWizardUiState {
  return state;
}

export function setSetupWizardUiState(
  patch: Partial<Omit<SetupWizardUiState, 'collapseInstant'>> & {
    collapseInstant?: boolean;
  },
): void {
  const next: SetupWizardUiState = {
    visible: patch.visible ?? state.visible,
    expanded: patch.expanded ?? state.expanded,
    collapseInstant: patch.collapseInstant ?? false,
  };
  if (
    next.visible === state.visible &&
    next.expanded === state.expanded &&
    next.collapseInstant === state.collapseInstant
  ) {
    return;
  }
  state = next;
  emit();
}

/** Accordion: companion expands → snap setup to strip. */
export function collapseSetupWizardPanel(): void {
  if (!state.visible || !state.expanded) return;
  state = { ...state, expanded: false, collapseInstant: true };
  emit();
}

export function subscribeSetupWizardUiState(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

/** @deprecated use getSetupWizardUiState().visible */
export function isSetupWizardVisible(): boolean {
  return state.visible;
}

/** @deprecated use setSetupWizardUiState */
export function setSetupWizardVisible(visible: boolean): void {
  setSetupWizardUiState(
    visible
      ? { visible: true, expanded: true, collapseInstant: false }
      : { visible: false, expanded: false, collapseInstant: false },
  );
}

/** @deprecated use subscribeSetupWizardUiState */
export function subscribeSetupWizardVisible(listener: () => void): () => void {
  return subscribeSetupWizardUiState(listener);
}

/* --- Personalized role create: setup accompanies until one role is created --- */

export type CustomRoleCreateEvent =
  | { type: 'started' }
  | { type: 'completed'; roleId: string }
  | { type: 'cancelled' };

type CustomRoleCreateListener = (ev: CustomRoleCreateEvent) => void;

const customRoleCreateListeners = new Set<CustomRoleCreateListener>();
let awaitingCustomRoleCreate = false;

export function isAwaitingCustomRoleCreate(): boolean {
  return awaitingCustomRoleCreate;
}

export function beginAwaitCustomRoleCreate(): void {
  awaitingCustomRoleCreate = true;
  for (const fn of customRoleCreateListeners) fn({ type: 'started' });
}

export function notifyCustomRoleCreated(roleId: string): void {
  if (!awaitingCustomRoleCreate) return;
  awaitingCustomRoleCreate = false;
  for (const fn of customRoleCreateListeners) fn({ type: 'completed', roleId });
}

/** User closed create drawer without saving (or abandoned). */
export function cancelAwaitCustomRoleCreate(): void {
  if (!awaitingCustomRoleCreate) return;
  awaitingCustomRoleCreate = false;
  for (const fn of customRoleCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeCustomRoleCreate(
  listener: CustomRoleCreateListener,
): () => void {
  customRoleCreateListeners.add(listener);
  return () => {
    customRoleCreateListeners.delete(listener);
  };
}

/* --- Roles bulk import: setup accompanies until import finishes or cancels --- */

export type RolesBulkImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type RolesBulkImportListener = (ev: RolesBulkImportEvent) => void;

const rolesBulkImportListeners = new Set<RolesBulkImportListener>();
let awaitingRolesBulkImport = false;

export function isAwaitingRolesBulkImport(): boolean {
  return awaitingRolesBulkImport;
}

export function beginAwaitRolesBulkImport(): void {
  awaitingRolesBulkImport = true;
  for (const fn of rolesBulkImportListeners) fn({ type: 'started' });
}

export function notifyRolesBulkImportDone(createdCount: number): void {
  if (!awaitingRolesBulkImport) return;
  awaitingRolesBulkImport = false;
  for (const fn of rolesBulkImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitRolesBulkImport(): void {
  if (!awaitingRolesBulkImport) return;
  awaitingRolesBulkImport = false;
  for (const fn of rolesBulkImportListeners) fn({ type: 'cancelled' });
}

export function subscribeRolesBulkImport(
  listener: RolesBulkImportListener,
): () => void {
  rolesBulkImportListeners.add(listener);
  return () => {
    rolesBulkImportListeners.delete(listener);
  };
}

/* --- Staff user create from setup Équipe: accompany until account created --- */

export type StaffUserCreateEvent =
  | { type: 'started'; roleName: string }
  | { type: 'completed'; userId: string; roleName: string }
  | { type: 'cancelled'; roleName: string };

type StaffUserCreateListener = (ev: StaffUserCreateEvent) => void;

const staffUserCreateListeners = new Set<StaffUserCreateListener>();
let awaitingStaffUserCreate: string | null = null;

export function isAwaitingStaffUserCreate(): boolean {
  return awaitingStaffUserCreate != null;
}

export function awaitingStaffUserCreateRole(): string | null {
  return awaitingStaffUserCreate;
}

export function beginAwaitStaffUserCreate(roleName: string): void {
  awaitingStaffUserCreate = roleName;
  for (const fn of staffUserCreateListeners) fn({ type: 'started', roleName });
}

export function notifyStaffUserCreated(userId: string): void {
  if (awaitingStaffUserCreate == null) return;
  const roleName = awaitingStaffUserCreate;
  awaitingStaffUserCreate = null;
  for (const fn of staffUserCreateListeners) fn({ type: 'completed', userId, roleName });
}

export function cancelAwaitStaffUserCreate(): void {
  if (awaitingStaffUserCreate == null) return;
  const roleName = awaitingStaffUserCreate;
  awaitingStaffUserCreate = null;
  for (const fn of staffUserCreateListeners) fn({ type: 'cancelled', roleName });
}

export function subscribeStaffUserCreate(
  listener: StaffUserCreateListener,
): () => void {
  staffUserCreateListeners.add(listener);
  return () => {
    staffUserCreateListeners.delete(listener);
  };
}

/* --- Staff users bulk import: setup accompanies until import finishes or cancels --- */

export type StaffUsersBulkImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type StaffUsersBulkImportListener = (ev: StaffUsersBulkImportEvent) => void;

const staffUsersBulkImportListeners = new Set<StaffUsersBulkImportListener>();
let awaitingStaffUsersBulkImport = false;

export function isAwaitingStaffUsersBulkImport(): boolean {
  return awaitingStaffUsersBulkImport;
}

export function beginAwaitStaffUsersImport(): void {
  awaitingStaffUsersBulkImport = true;
  for (const fn of staffUsersBulkImportListeners) fn({ type: 'started' });
}

export function notifyStaffUsersImportDone(createdCount: number): void {
  if (!awaitingStaffUsersBulkImport) return;
  awaitingStaffUsersBulkImport = false;
  for (const fn of staffUsersBulkImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitStaffUsersImport(): void {
  if (!awaitingStaffUsersBulkImport) return;
  awaitingStaffUsersBulkImport = false;
  for (const fn of staffUsersBulkImportListeners) fn({ type: 'cancelled' });
}

export function subscribeStaffUsersBulkImport(
  listener: StaffUsersBulkImportListener,
): () => void {
  staffUsersBulkImportListeners.add(listener);
  return () => {
    staffUsersBulkImportListeners.delete(listener);
  };
}

/* --- Marque create from setup: accompany until one marque is created --- */

export type MarkeCreateEvent =
  | { type: 'started' }
  | { type: 'completed' }
  | { type: 'cancelled' };

type MarkeCreateListener = (ev: MarkeCreateEvent) => void;

const markeCreateListeners = new Set<MarkeCreateListener>();
let awaitingMarkeCreate = false;

export function isAwaitingMarkeCreate(): boolean {
  return awaitingMarkeCreate;
}

export function beginAwaitMarkeCreate(): void {
  awaitingMarkeCreate = true;
  for (const fn of markeCreateListeners) fn({ type: 'started' });
}

export function notifyMarkeCreated(): void {
  if (!awaitingMarkeCreate) return;
  awaitingMarkeCreate = false;
  for (const fn of markeCreateListeners) fn({ type: 'completed' });
}

export function cancelAwaitMarkeCreate(): void {
  if (!awaitingMarkeCreate) return;
  awaitingMarkeCreate = false;
  for (const fn of markeCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeMarkeCreate(listener: MarkeCreateListener): () => void {
  markeCreateListeners.add(listener);
  return () => {
    markeCreateListeners.delete(listener);
  };
}

/* --- Marque provisioning via marques import from setup --- */

export type MarkeImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type MarkeImportListener = (ev: MarkeImportEvent) => void;

const markeImportListeners = new Set<MarkeImportListener>();
let awaitingMarkeImport = false;

export function isAwaitingMarkeImport(): boolean {
  return awaitingMarkeImport;
}

export function beginAwaitMarkeImport(): void {
  awaitingMarkeImport = true;
  for (const fn of markeImportListeners) fn({ type: 'started' });
}

export function notifyMarkeImportDone(createdCount: number): void {
  if (!awaitingMarkeImport) return;
  awaitingMarkeImport = false;
  for (const fn of markeImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitMarkeImport(): void {
  if (!awaitingMarkeImport) return;
  awaitingMarkeImport = false;
  for (const fn of markeImportListeners) fn({ type: 'cancelled' });
}

export function subscribeMarkeImport(listener: MarkeImportListener): () => void {
  markeImportListeners.add(listener);
  return () => {
    markeImportListeners.delete(listener);
  };
}

/* --- Product type create from setup --- */

export type ProductTypeCreateEvent =
  | { type: 'started' }
  | { type: 'completed' }
  | { type: 'cancelled' };

type ProductTypeCreateListener = (ev: ProductTypeCreateEvent) => void;

const productTypeCreateListeners = new Set<ProductTypeCreateListener>();
let awaitingProductTypeCreate = false;

export function isAwaitingProductTypeCreate(): boolean {
  return awaitingProductTypeCreate;
}

export function beginAwaitProductTypeCreate(): void {
  awaitingProductTypeCreate = true;
  for (const fn of productTypeCreateListeners) fn({ type: 'started' });
}

export function notifyProductTypeCreated(): void {
  if (!awaitingProductTypeCreate) return;
  awaitingProductTypeCreate = false;
  for (const fn of productTypeCreateListeners) fn({ type: 'completed' });
}

export function cancelAwaitProductTypeCreate(): void {
  if (!awaitingProductTypeCreate) return;
  awaitingProductTypeCreate = false;
  for (const fn of productTypeCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeProductTypeCreate(listener: ProductTypeCreateListener): () => void {
  productTypeCreateListeners.add(listener);
  return () => {
    productTypeCreateListeners.delete(listener);
  };
}

/* --- Product types import from setup --- */

export type ProductTypesImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type ProductTypesImportListener = (ev: ProductTypesImportEvent) => void;

const productTypesImportListeners = new Set<ProductTypesImportListener>();
let awaitingProductTypesImport = false;

export function isAwaitingProductTypesImport(): boolean {
  return awaitingProductTypesImport;
}

export function beginAwaitProductTypesImport(): void {
  awaitingProductTypesImport = true;
  for (const fn of productTypesImportListeners) fn({ type: 'started' });
}

export function notifyProductTypesImportDone(createdCount: number): void {
  if (!awaitingProductTypesImport) return;
  awaitingProductTypesImport = false;
  for (const fn of productTypesImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitProductTypesImport(): void {
  if (!awaitingProductTypesImport) return;
  awaitingProductTypesImport = false;
  for (const fn of productTypesImportListeners) fn({ type: 'cancelled' });
}

export function subscribeProductTypesImport(listener: ProductTypesImportListener): () => void {
  productTypesImportListeners.add(listener);
  return () => {
    productTypesImportListeners.delete(listener);
  };
}

/* --- Structure wizard (type + catégories) from setup --- */

export type StructureCreateEvent =
  | { type: 'started' }
  | { type: 'completed' }
  | { type: 'cancelled' };

type StructureCreateListener = (ev: StructureCreateEvent) => void;

const structureCreateListeners = new Set<StructureCreateListener>();
let awaitingStructureCreate = false;

export function isAwaitingStructureCreate(): boolean {
  return awaitingStructureCreate;
}

export function beginAwaitStructureCreate(): void {
  awaitingStructureCreate = true;
  for (const fn of structureCreateListeners) fn({ type: 'started' });
}

export function notifyStructureCreated(): void {
  if (!awaitingStructureCreate) return;
  awaitingStructureCreate = false;
  for (const fn of structureCreateListeners) fn({ type: 'completed' });
}

export function cancelAwaitStructureCreate(): void {
  if (!awaitingStructureCreate) return;
  awaitingStructureCreate = false;
  for (const fn of structureCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeStructureCreate(listener: StructureCreateListener): () => void {
  structureCreateListeners.add(listener);
  return () => {
    structureCreateListeners.delete(listener);
  };
}

/* --- Categories import from setup --- */

export type CategoriesImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type CategoriesImportListener = (ev: CategoriesImportEvent) => void;

const categoriesImportListeners = new Set<CategoriesImportListener>();
let awaitingCategoriesImport = false;

export function isAwaitingCategoriesImport(): boolean {
  return awaitingCategoriesImport;
}

export function beginAwaitCategoriesImport(): void {
  awaitingCategoriesImport = true;
  for (const fn of categoriesImportListeners) fn({ type: 'started' });
}

export function notifyCategoriesImportDone(createdCount: number): void {
  if (!awaitingCategoriesImport) return;
  awaitingCategoriesImport = false;
  for (const fn of categoriesImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitCategoriesImport(): void {
  if (!awaitingCategoriesImport) return;
  awaitingCategoriesImport = false;
  for (const fn of categoriesImportListeners) fn({ type: 'cancelled' });
}

export function subscribeCategoriesImport(listener: CategoriesImportListener): () => void {
  categoriesImportListeners.add(listener);
  return () => {
    categoriesImportListeners.delete(listener);
  };
}

export type LocationCreateEvent =
  | { type: 'started' }
  | { type: 'completed'; locationId: string }
  | { type: 'cancelled' };

type LocationCreateListener = (ev: LocationCreateEvent) => void;

const locationCreateListeners = new Set<LocationCreateListener>();
let awaitingLocationCreate = false;

export function isAwaitingLocationCreate(): boolean {
  return awaitingLocationCreate;
}

export function beginAwaitLocationCreate(): void {
  awaitingLocationCreate = true;
  for (const fn of locationCreateListeners) fn({ type: 'started' });
}

export function notifyLocationCreateDone(locationId: string): void {
  if (!awaitingLocationCreate) return;
  awaitingLocationCreate = false;
  for (const fn of locationCreateListeners) fn({ type: 'completed', locationId });
}

export function cancelAwaitLocationCreate(): void {
  if (!awaitingLocationCreate) return;
  awaitingLocationCreate = false;
  for (const fn of locationCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeLocationCreate(listener: LocationCreateListener): () => void {
  locationCreateListeners.add(listener);
  return () => {
    locationCreateListeners.delete(listener);
  };
}

export type ProductDetailTypeCreateEvent =
  | { type: 'started' }
  | { type: 'completed'; typeId: string }
  | { type: 'cancelled' };

type ProductDetailTypeCreateListener = (ev: ProductDetailTypeCreateEvent) => void;

const productDetailTypeCreateListeners = new Set<ProductDetailTypeCreateListener>();
let awaitingProductDetailTypeCreate = false;

export function isAwaitingProductDetailTypeCreate(): boolean {
  return awaitingProductDetailTypeCreate;
}

export function beginAwaitProductDetailTypeCreate(): void {
  awaitingProductDetailTypeCreate = true;
  for (const fn of productDetailTypeCreateListeners) fn({ type: 'started' });
}

export function notifyProductDetailTypeCreateDone(typeId: string): void {
  if (!awaitingProductDetailTypeCreate) return;
  awaitingProductDetailTypeCreate = false;
  for (const fn of productDetailTypeCreateListeners) fn({ type: 'completed', typeId });
}

export function cancelAwaitProductDetailTypeCreate(): void {
  if (!awaitingProductDetailTypeCreate) return;
  awaitingProductDetailTypeCreate = false;
  for (const fn of productDetailTypeCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeProductDetailTypeCreate(
  listener: ProductDetailTypeCreateListener,
): () => void {
  productDetailTypeCreateListeners.add(listener);
  return () => {
    productDetailTypeCreateListeners.delete(listener);
  };
}

export type OptionGroupCreateEvent =
  | { type: 'started' }
  | { type: 'completed'; groupId: string }
  | { type: 'cancelled' };

type OptionGroupCreateListener = (ev: OptionGroupCreateEvent) => void;

const optionGroupCreateListeners = new Set<OptionGroupCreateListener>();
let awaitingOptionGroupCreate = false;

export function isAwaitingOptionGroupCreate(): boolean {
  return awaitingOptionGroupCreate;
}

export function beginAwaitOptionGroupCreate(): void {
  awaitingOptionGroupCreate = true;
  for (const fn of optionGroupCreateListeners) fn({ type: 'started' });
}

export function notifyOptionGroupCreateDone(groupId: string): void {
  if (!awaitingOptionGroupCreate) return;
  awaitingOptionGroupCreate = false;
  for (const fn of optionGroupCreateListeners) fn({ type: 'completed', groupId });
}

export function cancelAwaitOptionGroupCreate(): void {
  if (!awaitingOptionGroupCreate) return;
  awaitingOptionGroupCreate = false;
  for (const fn of optionGroupCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeOptionGroupCreate(listener: OptionGroupCreateListener): () => void {
  optionGroupCreateListeners.add(listener);
  return () => {
    optionGroupCreateListeners.delete(listener);
  };
}

export type TagFamilyCreateEvent =
  | { type: 'started' }
  | { type: 'completed'; familyId: string }
  | { type: 'cancelled' };

type TagFamilyCreateListener = (ev: TagFamilyCreateEvent) => void;

const tagFamilyCreateListeners = new Set<TagFamilyCreateListener>();
let awaitingTagFamilyCreate = false;

export function isAwaitingTagFamilyCreate(): boolean {
  return awaitingTagFamilyCreate;
}

export function beginAwaitTagFamilyCreate(): void {
  awaitingTagFamilyCreate = true;
  for (const fn of tagFamilyCreateListeners) fn({ type: 'started' });
}

export function notifyTagFamilyCreateDone(familyId: string): void {
  if (!awaitingTagFamilyCreate) return;
  awaitingTagFamilyCreate = false;
  for (const fn of tagFamilyCreateListeners) fn({ type: 'completed', familyId });
}

export function cancelAwaitTagFamilyCreate(): void {
  if (!awaitingTagFamilyCreate) return;
  awaitingTagFamilyCreate = false;
  for (const fn of tagFamilyCreateListeners) fn({ type: 'cancelled' });
}

export function subscribeTagFamilyCreate(listener: TagFamilyCreateListener): () => void {
  tagFamilyCreateListeners.add(listener);
  return () => {
    tagFamilyCreateListeners.delete(listener);
  };
}

export type TagsBulkImportEvent =
  | { type: 'started' }
  | { type: 'completed'; createdCount: number }
  | { type: 'cancelled' };

type TagsBulkImportListener = (ev: TagsBulkImportEvent) => void;

const tagsBulkImportListeners = new Set<TagsBulkImportListener>();
let awaitingTagsBulkImport = false;

export function isAwaitingTagsBulkImport(): boolean {
  return awaitingTagsBulkImport;
}

export function beginAwaitTagsBulkImport(): void {
  awaitingTagsBulkImport = true;
  for (const fn of tagsBulkImportListeners) fn({ type: 'started' });
}

export function notifyTagsBulkImportDone(createdCount: number): void {
  if (!awaitingTagsBulkImport) return;
  awaitingTagsBulkImport = false;
  for (const fn of tagsBulkImportListeners) fn({ type: 'completed', createdCount });
}

export function cancelAwaitTagsBulkImport(): void {
  if (!awaitingTagsBulkImport) return;
  awaitingTagsBulkImport = false;
  for (const fn of tagsBulkImportListeners) fn({ type: 'cancelled' });
}

export function subscribeTagsBulkImport(listener: TagsBulkImportListener): () => void {
  tagsBulkImportListeners.add(listener);
  return () => {
    tagsBulkImportListeners.delete(listener);
  };
}
